import React, { FC, useState } from 'react';
import Box from '@material-ui/core/Box';
import Checkbox from '@material-ui/core/Checkbox';
import IconButton from '@material-ui/core/IconButton';
import HistoryIcon from '@material-ui/icons/History';
import { makeStyles } from '@material-ui/core/styles';
import { InjectedFieldProps, PublicFieldProps } from 'react-admin';
import { JobPhaseReceivableSheetInterface } from '@vatos-pas/common';

import { PermissionsProvider } from 'providers/permissionsProvider';
import {
  CAN_EDIT_RECEIVABLE,
  CAN_SEE_RECEIVABLE_HISTORY,
} from 'providers/permissions';
import { Show } from 'components/Show';

import { useReceivableCheckboxField } from '../hooks/useReceivableCheckboxField';
import {
  ReceivableHistoryModal,
  ReceivableHistoryVariant,
} from './ReceivableHistoryModal';

type CheckboxFieldKey = 'supplyProIntegrated' | 'billed';

// History modal variant + human-readable name (for the history aria-label) are
// both fully determined by the field, so callers only pass `field`.
const FIELD_CONFIG: Record<
  CheckboxFieldKey,
  { variant: ReceivableHistoryVariant; name: string }
> = {
  supplyProIntegrated: { variant: 'supplyPro', name: 'SupplyPro' },
  billed: { variant: 'billed', name: 'Billed' },
};

type ReceivableCheckboxFieldProps = PublicFieldProps &
  InjectedFieldProps<JobPhaseReceivableSheetInterface> & {
    field: CheckboxFieldKey;
  };

const useStyles = makeStyles({
  historyIcon: {
    color: '#757575',
  },
});

export const ReceivableCheckboxField: FC<ReceivableCheckboxFieldProps> = ({
  record,
  field,
}) => {
  const classes = useStyles();
  const { variant, name } = FIELD_CONFIG[field];
  const { hasPermission } = PermissionsProvider.useContainer();
  const { checked, receivableId, onChange } = useReceivableCheckboxField(
    record,
    field,
  );
  const [historyOpen, setHistoryOpen] = useState(false);

  if (!record) return null;

  const canEdit = hasPermission(CAN_EDIT_RECEIVABLE);
  const canSeeHistory = hasPermission(CAN_SEE_RECEIVABLE_HISTORY);

  return (
    <Box display="flex" alignItems="center" minWidth={80}>
      <Checkbox
        checked={checked}
        disabled={!canEdit}
        onChange={event => {
          // onChange is fire-and-forget; it handles its own errors/revert.
          void onChange(event);
        }}
      />
      <Show condition={canSeeHistory}>
        <IconButton
          size="small"
          onClick={() => setHistoryOpen(true)}
          aria-label={`View ${name} change history`}
        >
          <HistoryIcon className={classes.historyIcon} />
        </IconButton>
      </Show>
      <ReceivableHistoryModal
        open={historyOpen}
        jobPhaseReceivableId={receivableId}
        variant={variant}
        onClose={() => setHistoryOpen(false)}
      />
    </Box>
  );
};

export default ReceivableCheckboxField;
